import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Flashbar,
  KeyValuePairs,
  Modal,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
  TextContent,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { STATUS_INACTIVE, STATUS_PENDING, TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { useLocation } from 'react-router-dom';
import { createFgaPolicy, deleteFgaPolicy, listFgaPolicies } from 'src/api/permissions';
import { createFgaPolicyViewLink, createWorkspaceDetailLink, createWSPermissionPublishedLink, Page } from 'src/routes';
import { EmptyState } from 'src/commons/EmptyState';
import { defaultWrapLinesPreference, paginationLabels } from 'src/commons/tables';
import { StatusIcon } from 'src/components/permissions/myDatasets/statusIcon';

import { PageHeader } from 'src/components/common/PageHeader';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface FineGrainAccessPoliciesProps extends CommonPageProps {}

export const FineGrainAccessPolicies = (props: FineGrainAccessPoliciesProps) => {
  const { state } = useLocation();
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [ownerId, setOwnerId] = useState(undefined);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [deletePolicyModalVisible, setDeletePolicyModalVisible] = useState(false);
  const [viewPolicyModalVisible, setViewPolicyModalVisible] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(undefined);
  const [selectedPolicyId, setSelectedPolicyId] = useState(undefined);
  const [fgaPolicies, setFgaPolicies] = useState([]);
  const [tableMessage, setTableMessage] = useState('No policies');
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });
  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(fgaPolicies, {
    filtering: {
      noMatch: '',
      empty: <EmptyState title={tableMessage} subtitle={'No policies found.'} />,
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  useEffect(() => {
    const { selectedItems } = collectionProps;
    const selectedPolicy = selectedItems[0];
    if (selectedPolicy) {
      setSelectedPolicy(selectedPolicy);
      setSelectedPolicyId(selectedPolicy.id);
    }
  }, [collectionProps.selectedItems]);

  const fgaPolicyColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'policyName',
      header: 'Policy name',
      cell: (item) => <Link href={createFgaPolicyViewLink(item?.id)}>{item?.tableData?.name}</Link>,
      minWidth: 200,
      sortingComparator: (a, b) => {
        return a?.tableData?.name.localeCompare(b?.tableData?.name);
      },
    },
    {
      id: 'resource',
      header: 'Resource',
      cell: (item) =>
        item?.tableData
          ? `${item.tableData.tableCatalogId}.${item.tableData.databaseName}.${item.tableData.tableName}`
          : 'NA',
      minWidth: 400,
    },
    {
      id: 'type',
      header: 'Type',
      cell: (item) => item.type,
      minWidth: 100,
      sortingField: 'type',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => <StatusIcon status={item?.status} />,
      minWidth: 100,
    },
  ];

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);

    const activeWorkspace = props.activeWorkspace;
    if (activeWorkspace) {
      const ownerId = props.match.params.id ? props.match.params.id : activeWorkspace.workspaceId;
      setOwnerId(ownerId);

      if (state) {
        setNotification(state.status, state.message);
      }
    } else {
      props.setRedirect(Page.HOME);
    }
  }, []);

  useEffect(() => {
    if (ownerId) {
      getFgaPoliciesForWorkspace();
    }
  }, [ownerId]);

  const getFgaPoliciesForWorkspace = async () => {
    setLoadingPolicies(true);
    try {
      // get both LF and Redshift policies
      let listFgaPoliciesForWorkspaceResult = await listFgaPolicies({
        ownerId: ownerId,
      });
      let policies = listFgaPoliciesForWorkspaceResult.FineGrainAccess;
      while (listFgaPoliciesForWorkspaceResult.nextToken != undefined) {
        listFgaPoliciesForWorkspaceResult = await listFgaPolicies({
          ownerId: ownerId,
          nextToken: listFgaPoliciesForWorkspaceResult.nextToken,
        });
        policies.push(...listFgaPoliciesForWorkspaceResult.FineGrainAccess);
      }
      setFgaPolicies(
        policies.filter((policy) => {
          return policy != undefined;
        }),
      );
    } catch (err) {
      setTableMessage(`Unable to load policies. ${err.message}`);
    } finally {
      setLoadingPolicies(false);
    }
  };

  const handleRefresh = async () => {
    setLoadingPolicies(true);
    setSelectedPolicy(undefined);
    setSelectedPolicyId(undefined);
    await getFgaPoliciesForWorkspace();
  };

  const handleCreateFgaPolicy = async () => {
    try {
      let createFgaPolicyRequest = {
        ownerId: selectedPolicy.ownerId,
        description: selectedPolicy.description,
        scope: 'Dataset', //can be changed if needed but scoped always to dataset
        visibility: selectedPolicy.visibility,
        tableData: selectedPolicy.tableData,
      };
      const createFgaPolicyResponse = await createFgaPolicy(createFgaPolicyRequest);
      if (createFgaPolicyResponse.failures) {
        setNotification('error', 'Failure in creating policy');
      } else {
        setNotification('success', 'Submitted a request to retry the policy creation');
      }
    } catch (err) {
      setNotification('error', `Error in creating policy:${err.message}`);
    }
  };

  const handleEditFgaPolicy = async () => {
    if (selectedPolicy !== undefined) {
      props.setRedirect(Page.EDIT_FINE_GRAIN_ACCESS_POLICY, false, {
        fgaPolicyId: selectedPolicyId,
        workspaceId: ownerId,
      });
    } else {
      setNotification('error', 'Please select a policy to edit');
    }
  };

  const handleDeleteFgaPolicy = async () => {
    setButtonLoading(true);
    try {
      let deleteFgaPolicyResult = await deleteFgaPolicy({
        id: selectedPolicy.id,
      });
      await handleRefresh();
      if (deleteFgaPolicyResult.failures) {
        setNotification('error', 'Failed to delete the policy.');
      } else {
        setNotification('success', 'Success');
      }
    } catch (err) {
      setNotification('error', `Error in deleting the policy. ${err.message}`);
    } finally {
      setButtonLoading(false);
      setDeletePolicyModalVisible(false);
    }
  };

  const buttonOptions = () => {
    return [
      {
        text: '',
        icon: 'refresh',
        onItemClick: handleRefresh,
      },
      {
        text: 'Actions',
        onItemClick: handleButtonAction,
        items: [
          {
            text: 'View',
            id: 'viewPolicy',
            disabled: !selectedPolicyId,
          },
          {
            text: 'Edit',
            id: 'editPolicy',
            disabled: !selectedPolicyId || selectedPolicy?.status == STATUS_PENDING,
          },
          {
            text: 'Delete',
            id: 'deletePolicy',
            disabled: !selectedPolicyId || selectedPolicy?.status == STATUS_PENDING,
          },
          {
            text: 'Retry creation',
            id: 'recreatePolicy',
            disabled: selectedPolicy?.status != STATUS_INACTIVE,
          },
        ],
      },
      {
        text: 'Create policy',
        variant: 'primary',
        onItemClick: () => props.setRedirect(Page.CREATE_FINE_GRAIN_ACCESS_POLICY),
      },
    ];
  };

  const handleButtonAction = async (e) => {
    if (e.detail.id === 'editPolicy') {
      handleEditFgaPolicy();
    } else if (e.detail.id === 'deletePolicy') {
      setDeletePolicyModalVisible(true);
    } else if (e.detail.id === 'viewPolicy') {
      setViewPolicyModalVisible(true);
    } else if (e.detail.id == 'recreatePolicy') {
      handleCreateFgaPolicy();
    }
  };

  const setNotification = async (header, message) => {
    if (header === 'success') {
      setNotifications([
        {
          type: 'success',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    } else {
      setNotifications([
        {
          header: header,
          type: 'error',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  return (
    <div>
      <Flashbar items={notifications} />
      <Modal
        visible={deletePolicyModalVisible}
        header={[`Confirm deletion`]}
        onDismiss={() => setDeletePolicyModalVisible(false)}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setDeletePolicyModalVisible(false);
                }}
              >
                Cancel
              </Button>
              <Button variant='primary' loading={buttonLoading} onClick={handleDeleteFgaPolicy}>
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <TextContent>
          Are you sure you want to delete the <strong>{selectedPolicyId}</strong> policy from{' '}
          <strong>{props?.activeWorkspace?.accountId}</strong> account?
          <br />
          <br />
          <p>This will also delete the policy from the associated AWS account.</p>
        </TextContent>
      </Modal>

      <Modal
        visible={viewPolicyModalVisible}
        header={[`Policy details`]}
        onDismiss={() => setViewPolicyModalVisible(false)}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setViewPolicyModalVisible(false);
                }}
              >
                Cancel
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <div>
          <KeyValuePairs
            columns={3}
            items={[
              { label: 'Policy ID', value: selectedPolicy?.id },
              {
                label: 'Owner',
                value: <Link href={createWorkspaceDetailLink(selectedPolicy?.ownerId)}>{selectedPolicy?.ownerId}</Link>,
              },
              { label: 'Description', value: selectedPolicy?.description },
              { label: 'Scope', value: selectedPolicy?.scope },
              { label: 'Type', value: selectedPolicy?.type },
              {
                label: 'Data permission',
                value: (
                  <Link href={createWSPermissionPublishedLink(selectedPolicy?.dataPermissionId)}>
                    {selectedPolicy?.dataPermissionId}
                  </Link>
                ),
              },
              { label: 'Created on', value: selectedPolicy?.createDate },
              { label: 'Updated on', value: selectedPolicy?.updateDate },
              selectedPolicy?.tableDate?.clusterId
                ? { label: 'Cluster identifier', value: selectedPolicy?.tableData?.clusterId }
                : undefined,
              { label: 'Database name', value: selectedPolicy?.tableData?.databaseName },
              selectedPolicy?.tableDate?.clusterId
                ? { label: 'Schema name', value: selectedPolicy?.tableData?.schemaName }
                : undefined,
              { label: 'Table name', value: selectedPolicy?.tableData?.tableName },
              {
                label: 'Column-level access',
                value: selectedPolicy?.tableData?.columnNames
                  ? 'Include'
                  : selectedPolicy?.tableData?.columnWildcard?.excludedColumnNames
                  ? 'Exclude'
                  : '*',
              },
              {
                label: 'Row filter expression',
                value: selectedPolicy?.tableData?.rowFilter?.filterExpression
                  ? selectedPolicy.tableData.rowFilter.filterExpression
                  : '*',
              },
              {
                label: 'Columns',
                value: selectedPolicy?.tableData?.columnNames
                  ? selectedPolicy?.tableData?.columnNames.join(',')
                  : selectedPolicy?.tableData?.columnWildcard?.excludedColumnNames
                  ? selectedPolicy?.tableData?.columnWildcard?.excludedColumnNames.join(',')
                  : null,
              },
            ].filter((item) => item != undefined)}
          />
        </div>
      </Modal>

      <Table
        {...collectionProps}
        loadingText='Loading policies...'
        loading={loadingPolicies}
        columnDefinitions={fgaPolicyColumnDefinitions}
        items={items}
        selectionType='single'
        variant='stacked'
        empty={<EmptyState title='No policies' subtitle='No policies to display.' />}
        wrapLines={false}
        resizableColumns={true}
        header={<PageHeader buttons={buttonOptions()} header={'Fine grain access policies'} />}
        pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringAriaLabel='Filter resources'
            filteringPlaceholder='Find resources'
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={{
              title: 'Page size',
              options: [
                { value: 25, label: '25 items' },
                { value: 50, label: '50 items' },
                { value: 100, label: '100 items' },
              ],
            }}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
      />
    </div>
  );
};

export default FineGrainAccessPolicies;
